import { useMutation, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { EdgeConfigurationUpdate } from '@cast/types-omni';

import { getEdgeConfigurationsQueryKey } from 'hooks/queries/omni/useListEdgeConfigurationsQuery';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { getEdgeConfigurationQueryKey } from 'hooks/queries/omni/useGetEdgeConfigurationQuery';

type UseUpdateEdgeConfigurationMutationProps = {
  edgeConfigurationId: string;
  edgeLocationId: string;
  clusterId: string;
  onSuccess?: () => void;
};

export const useUpdateEdgeConfigurationMutation = ({
  edgeConfigurationId,
  edgeLocationId,
  clusterId,
  onSuccess,
}: UseUpdateEdgeConfigurationMutationProps) => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizations();

  return useMutation({
    mutationFn: (edgeConfiguration: EdgeConfigurationUpdate) => {
      return apiClient.omni.updateEdgeConfiguration({
        organizationId: currentOrganization!.id,
        clusterId,
        edgeLocationId,
        id: edgeConfigurationId,
        edgeConfigurationUpdate: edgeConfiguration,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getEdgeConfigurationsQueryKey({}),
      });
      queryClient.invalidateQueries({
        queryKey: getEdgeConfigurationQueryKey({
          organizationId: currentOrganization?.id,
          clusterId,
          edgeLocationId,
          edgeConfigurationId,
        }),
      });
      notify.success('Edge configuration updated successfully!');
      onSuccess?.();
    },
    onError: (error: unknown) => {
      notify.error(parseApiError(error));
    },
  });
};
